import React, { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import StatusBadge from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { GraduationCap } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const STATUS_LABELS = {
  pending: "Awaiting Payment",
  awaiting_scholarship_verification: "Awaiting Scholarship Verification",
  pending_approval: "Pending Approval",
  registered: "Confirmed",
  confirmed: "Confirmed",
  waitlisted: "Waitlisted",
  cancelled: "Cancelled",
};

export default function Payments() {
  const [regs, setRegs] = useState([]);
  const load = () => api.get("/registrations/me").then((r) => setRegs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reserved = async (id) => {
    try { await api.put(`/registrations/${id}/reserved-funds`); toast.success("Thanks — we'll verify your reservation."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <h1 className="font-display text-3xl font-bold text-navy">Payments & Scholarships</h1>
      <p className="text-slateBrand">Your registrations, receipts and scholarship status.</p>

      <div className="mt-8 space-y-4">
        {regs.length === 0 && <p className="text-sm text-slateBrand">No payments yet.</p>}
        {regs.map((r) => {
          const sufs = r.payment_method === "sufs";
          return (
            <div key={r.id} data-testid={`payment-item-${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-bold text-navy">{r.experience?.title}</div>
                  <div className="text-xs text-slateBrand">{new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} • {sufs ? "Step Up For Students" : (r.payment_type || "").replace("_", " ")}</div>
                </div>
                <StatusBadge status={sufs && !r.scholarship?.verified ? "pending" : r.payment_status} label={STATUS_LABELS[r.status] || undefined} />
              </div>
              <div className="mt-4 space-y-1 border-t border-slate-100 pt-4">
                {(r.line_items || []).map((li, i) => (
                  <div key={i} className="flex justify-between text-sm text-slateBrand">
                    <span>{li.label}{li.quantity > 1 ? ` × ${li.quantity}` : ""}</span>
                    <span className="font-semibold text-navy">${(li.amount * (li.quantity || 1)).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-slate-100 pt-2 font-display font-bold text-navy">
                  <span>{sufs ? "Service Total" : "Total"}</span><span data-testid={`payment-total-${r.id}`}>${(r.amount || 0).toFixed(2)}</span>
                </div>
              </div>

              {sufs && (
                <div className="mt-4 rounded-xl bg-gold/10 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#8A5F00]"><GraduationCap className="h-4 w-4" />Scholarship status</div>
                  <p className="mt-1 text-xs text-[#8A5F00]">
                    {r.scholarship?.verified ? "Verified by H.E.A." : r.scholarship?.reservation_submitted ? "Reservation submitted — awaiting H.E.A. verification." : "Reserve your funds in EMA, then let us know below."}
                  </p>
                  {!r.scholarship?.reservation_submitted && !r.scholarship?.verified && (
                    <Button size="sm" data-testid={`reserved-funds-${r.id}`} onClick={() => reserved(r.id)} className="mt-3 bg-navy font-display text-xs font-semibold text-white hover:bg-navy/90">I Have Reserved My Funds</Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </AppShell>
  );
}
